import { useLocation, useMatch } from '@tanstack/react-router';
import { useRef } from 'react';
import { isSubRoutePath } from './previous-pathname';

/**
 * The pathname the calling component mounted on, for as long as the location stays on that path or
 * one of its sub-routes; then the calling route's own pathname while the location stays in that
 * route's subtree; the live pathname once it moves elsewhere.
 *
 * Page-level hooks need "the pathname this page lives on", not the whole location. Subscribing to the
 * whole location (`useLocation()`) re-renders the page, and with antd every table row, whenever one
 * of its own overlays (a record drawer rendered through its `<Outlet />`) opens or closes. The
 * selector returns a string, so the caller re-renders only when that string changes.
 *
 * The route fallback covers a list that mounts on one of its own record URLs (a link or reload
 * straight into a drawer): leaving that record re-renders the list once, and opening and closing
 * other records does not. The value is then never the mounted pathname, so `usePathState` keeps its
 * storage key and does not write under a record path that path-state cleanup has pruned. Reopening
 * the record it mounted on re-renders the list, because that key is live again there.
 */
export function useMountedPagePathname(): string {
    const mountedRef = useRef<string | null>(null);
    const routePathname = useMatch({ strict: false, shouldThrow: false, select: (match) => match.pathname });
    const pathname = useLocation({
        select: (location) => {
            const mounted = mountedRef.current;
            if (mounted === null) return location.pathname;
            if (location.pathname === mounted || isSubRoutePath(location.pathname, mounted)) return mounted;
            if (routePathname === undefined) return location.pathname;
            // An index route's pathname ends in `/`; the location's usually does not.
            const route = withoutTrailingSlash(routePathname);
            return withoutTrailingSlash(location.pathname) === route || isSubRoutePath(location.pathname, route)
                ? route
                : location.pathname;
        },
    });
    mountedRef.current ??= pathname;
    return pathname;
}

function withoutTrailingSlash(pathname: string): string {
    return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}
